import React, { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Context from "../../store/context";
import JsonCollapse from "./JsonCollapse";
import Input from "../nodes/Logic/Inputs/Input";
import timezones from "../nodes/Logic/timezones";
import {
  selectProjectData,
  setProjectData as setPRData,
} from "../../store/slices/projectSlice";
import { categories, industryTypes } from "../../helpers/categories";
import ProjectExportModal from "./ProjectExportModal";
import MyModal from "../../Common/MyModal";
import { useDeleteProjectMutation } from "../../store/slices/settingsSlice";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar, faSpinner } from "@fortawesome/free-solid-svg-icons";
import HelpButton from "../../HelpContent/HelpButton";
import ScheduleSettings from "../../Schedulers/ScheduleSettings";
import { useConfirmDialog } from "./ConfirmDialogContext";

export default function ProjectSettings() {
  const { countries, defaultCountry, defaultTZ } = JSON.parse(
    document.querySelector("#app").dataset.page
  ).props;
  const { saveProject } = useContext(Context);
  const projectData = useSelector(selectProjectData);
  const dispatch = useDispatch();
  const setProjectData = (fn) => dispatch(setPRData(fn(projectData)));
  const navigate = useNavigate();
  const params = useParams();
  const [deleteProject] = useDeleteProjectMutation();
  const confirmDialog = useConfirmDialog();
  const [loading, setLoading] = useState(false);
  return (
    <div className="position-absolute w-100 vh-100 page-bg">
      <div className="bg-loop row mx-0 mb-5 align-items-center">
        <div className="col-4 p-5">
          <h1 className="fw-bold">
            Free Build
            <HelpButton helpKey="free-build" />
          </h1>
          <span className="text-secondary">boomLogic project settings</span>
        </div>
        <div className="col-4 p-5">
          <h3 className="fw-bold text-center">{projectData.Label}</h3>
          <div className="m-auto d-flex justify-content-center gap-3">
            <div className="text-secondary text-nowrap">
              <span className="fw-bold">Boomerang ref:</span>
              {projectData.UUID}
            </div>
            <div className="text-secondary text-nowrap">
              <span className="fw-bold">Your ref:</span>
              {projectData.Reference}
            </div>
          </div>
        </div>
      </div>
      <div className="row my-5 mx-0">
        <div className="offset-md-2 col-md-4 border-end border-1">
          <div className="row">
            <div className="offset-md-2 col-md-9">
              {[
                { label: "Project label", field: "Label" },
                { label: "Project reference", field: "Reference" },
              ].map((i) => (
                <div className="mb-4" key={i.field}>
                  <h5 className="form-label">{i.label}</h5>
                  <input
                    style={{ height: 40 }}
                    className="form-control mb-2"
                    type="text"
                    value={projectData?.[i.field] ?? ""}
                    onChange={(e) =>
                      setProjectData((prev) => ({
                        ...prev,
                        [i.field]: e.target.value,
                      }))
                    }
                  />
                </div>
              ))}
              <div className="mb-4">
                <h5 className="form-label">Project description</h5>
                <textarea
                  maxLength={255}
                  className="form-control mb-2"
                  value={projectData?.Description ?? ""}
                  onChange={(e) =>
                    setProjectData((prev) => ({
                      ...prev,
                      Description: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="mb-4">
                <h5 className="form-label">Status</h5>
                <Input
                  inputType="select"
                  isValidNewOption={() => false}
                  value={[projectData?.Status]}
                  onChange={(values) =>
                    setProjectData((prev) => ({
                      ...prev,
                      Status: values[0],
                    }))
                  }
                  options={[
                    {
                      value: "draft",
                      label: `Draft`,
                    },
                    {
                      value: "production",
                      label: `Production`,
                    },
                    {
                      value: "archived",
                      label: `Archived`,
                    },
                  ]}
                />
              </div>
              <div className="mb-4">
                <h5 className="form-label">Allow only one instance running</h5>
                <Input
                  inputType="radio"
                  radioName="isSingleton"
                  isValidNewOption={() => false}
                  value={[projectData?.IsSingleton || false]}
                  onChange={(values) =>
                    setProjectData((prev) => ({
                      ...prev,
                      IsSingleton: values[0],
                    }))
                  }
                  options={[
                    {
                      value: false,
                      label: `No`,
                    },
                    {
                      value: true,
                      label: `YES`,
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="row">
            <div className="offset-md-1 col-md-9">
              <div className="mb-4">
                <h5 className="form-label">Default time zone</h5>
                <Input
                  inputType="select"
                  isValidNewOption={() => false}
                  value={[projectData?.TimeZone || ""]}
                  onChange={(values) =>
                    setProjectData((prev) => ({
                      ...prev,
                      TimeZone: values[0],
                    }))
                  }
                  options={[
                    {
                      value: "",
                      label: `Default from product settings: ${defaultTZ}`,
                    },
                    ...timezones.map((tz) => ({
                      value: tz.tzCode,
                      label: tz.label,
                    })),
                  ]}
                />
              </div>
              <div className="mb-4">
                <h5 className="form-label">Default Country</h5>
                <Input
                  inputType="select"
                  isValidNewOption={() => false}
                  value={[projectData?.CountryISO || ""]}
                  onChange={(values) =>
                    setProjectData((prev) => ({
                      ...prev,
                      CountryISO: values[0],
                    }))
                  }
                  options={[
                    {
                      value: "",
                      label: `Default from product settings: ${defaultCountry.country_name}`,
                    },
                    ...countries.map((c) => ({
                      value: c.country_code,
                      label: c.country_name,
                    })),
                  ]}
                />
              </div>
              <div className="mb-4">
                <h5 className="form-label">Industry</h5>
                <Input
                  inputType="select"
                  isValidNewOption={(o) => o}
                  value={[projectData?.Industry || ""]}
                  onChange={(values) =>
                    setProjectData((prev) => ({
                      ...prev,
                      Industry: values[0],
                    }))
                  }
                  options={[
                    {
                      value: "",
                      label: `Default from product settings: ${defaultCountry.country_name}`,
                    },
                    ...industryTypes,
                  ]}
                />
              </div>
              <div className="mb-4">
                <h5 className="form-label">Category</h5>
                <Input
                  inputType="select"
                  isValidNewOption={(o) => o}
                  value={[projectData?.Category || ""]}
                  onChange={(values) =>
                    setProjectData((prev) => ({
                      ...prev,
                      Category: values[0],
                    }))
                  }
                  options={[
                    {
                      value: "",
                      label: `Default from product settings: ${defaultCountry.country_name}`,
                    },
                    ...categories,
                  ]}
                />
              </div>
              {!projectData?.SolutionUuid && (
                <div className="mb-4">
                  <h5 className="form-label">Component part of a solution</h5>
                  <Input
                    isDisabled={projectData?.SolutionUuid}
                    inputType="radio"
                    radioName="isSolution"
                    isValidNewOption={() => false}
                    value={[
                      projectData?.SolutionUuid
                        ? false
                        : projectData?.IsSolution || false,
                    ]}
                    onChange={(values) =>
                      setProjectData((prev) => ({
                        ...prev,
                        IsSolution: values[0],
                      }))
                    }
                    options={[
                      {
                        value: false,
                        label: `No`,
                      },
                      {
                        value: true,
                        label: `YES`,
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="m-auto d-flex justify-content-center gap-2">
        <button
          type="button"
          className="btn btn-colored"
          onClick={() => navigate(`/library/logic/builder/${params.projectID}`)}
        >
          Edit component
        </button>
        <MyModal
          heading={
            <span>
              Export this component <HelpButton helpKey="export-component" />
            </span>
          }
          size="lg"
          label="Export component"
          buttonClassName="btn btn-colored rounded-0 px-2"
        >
          <ProjectExportModal
            projectId={params.projectID}
            projectName={projectData?.Label}
          />
        </MyModal>
        <button type="button" className="btn btn-colored" onClick={saveProject}>
          Save component
        </button>
      </div>
      <div className="m-auto d-flex justify-content-center  mt-5">
        <button
          type="button"
          className="btn btn-danger my-4"
          onClick={async () => {
            if (
              (await confirmDialog(
                "Remove project",
                `Are you sure you want to remove this project?`
              )) === false
            ) {
              return;
            }
            setLoading(true);
            await deleteProject({ id: params.projectID }).then((res) => {
              if ("data" in res) {
                toast.success("Project deleted successfully");
              }
              setTimeout(() => {
                if ("data" in res) {
                  navigate("/library/logic");
                }
                setLoading(false);
              }, 700);
            });
          }}
          disabled={loading}
        >
          Delete component
          {loading ? (
            <FontAwesomeIcon icon={faSpinner} spin className="ms-2" />
          ) : (
            ""
          )}
        </button>
      </div>
      <JsonCollapse json={projectData} />
    </div>
  );
}
